// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { EventsByFeature } from '@evolvemep/api-ts';
import { Card, Flex, Stack } from '@mantine/core';

import { isNil } from 'helpers/isNotNil';
import { SummaryCard } from 'modules/Shop/Insights/RevitUsage/components/SummaryCard';
import type { AnalyticsViewProps } from 'modules/Shop/Insights/RevitUsage/types';
import { FeatureUsageChart } from 'modules/Shop/Insights/RevitUsage/views/charts/FeatureUsageChart';

export const FeatureHeatmapView = (props: AnalyticsViewProps) => {
  const [data, setData] = useState<EventsByFeature[]>();

  const mostEvents = data?.reduce(
    (out, curr) => (curr.totalUsage > out.totalUsage ? curr : out),
    data[0] as EventsByFeature,
  );
  const tiesWithMost = data?.filter(
    (d) => d.totalUsage === mostEvents?.totalUsage && d.featureTypeName !== mostEvents.featureTypeName,
  ).length;
  const leastEvents = data?.reduce(
    (out, curr) => (curr.totalUsage < out.totalUsage ? curr : out),
    data[0] as EventsByFeature,
  );
  const tiesWithLeast = data?.filter(
    (d) => d.totalUsage === leastEvents?.totalUsage && d.featureTypeName !== leastEvents.featureTypeName,
  ).length;

  return (
    <Stack gap="sm" flex={1} style={{ overflowY: 'auto' }}>
      <Flex gap="sm">
        <SummaryCard
          title="Total Events"
          value={data?.reduce((out, curr) => out + curr.totalUsage, 0)}
          subtitle={`Across ${data?.length ?? '--'} Features`}
          loading={isNil(data)}
        />
        <SummaryCard
          title="Most Events"
          value={mostEvents?.totalUsage}
          subtitle={`${mostEvents?.featureTypeName} ${tiesWithMost !== 0 ? `(+${tiesWithMost} more)` : ''}`}
          loading={isNil(data)}
        />
        <SummaryCard
          title="Least Events"
          value={leastEvents?.totalUsage}
          subtitle={`${leastEvents?.featureTypeName} ${tiesWithLeast !== 0 ? `(+${tiesWithLeast} more)` : ''}`}
          loading={isNil(data)}
        />
      </Flex>
      <Card withBorder flex={1}>
        <FeatureUsageChart onDataRealized={setData} {...props} />
      </Card>
    </Stack>
  );
};
